import type { PlayerState, Point } from './types';
import type { GroundProbe } from './shot-geometry';
import { ORIGINAL_ACTION_TICK_MS } from './attack-timing';

export const SOURCE_WALK_STEP = 3;
export const SOURCE_WALK_HEIGHT = 7;
export const SOURCE_EDGE_STEP = 2;
export const SOURCE_FALL_STEP = 12;
export interface WalkGround extends GroundProbe { width: number; height: number }
export interface WalkFall { from: Point; to: Point; duration: number; hpBeforeDeath?: number }

export function sourceFallDuration(fromY: number, toY: number): number {
  return Math.ceil(Math.max(0, toY - fromY) / SOURCE_FALL_STEP) * ORIGINAL_ACTION_TICK_MS;
}

/** Interpolate between the source's12-pixel25fps ticks, including the last partial tick. */
export function sourceFallY(fromY: number, toY: number, elapsed: number): number {
  const drop = Math.max(0, toY - fromY), ticks = Math.max(0, elapsed) / ORIGINAL_ACTION_TICK_MS;
  const lower = Math.min(drop, Math.floor(ticks) * SOURCE_FALL_STEP);
  const upper = Math.min(drop, (Math.floor(ticks) + 1) * SOURCE_FALL_STEP);
  return fromY + lower + (upper - lower) * (ticks % 1);
}

/** Matching Flash Map.findYLineNotEmptyPointDown and server Map.FindYLineNotEmptyPoint:
 * the foot is the first solid x±1 row, excluding the final bitmap row. */
export function sourceStandingY(ground: WalkGround, x: number, fromY = 0): number | null {
  const column = Math.max(0, Math.min(ground.width - 1, Math.trunc(x)));
  for (let y = Math.max(0, Math.trunc(fromY)); y < ground.height - 1; y++) {
    if (ground.isSolid(column - 1, y) || ground.isSolid(column + 1, y)) return y;
  }
  return null;
}

/** Pinned SelfPlayerWalkAction / Map.findNextWalkPoint. Batched commands stop
 * at the first edge; PlayerFallingAction owns vertical motion from that point.
 * The caller validates turn/fall timing; this function only resolves geometry. */
export function sourceWalk(player: PlayerState, dx: number, ground: WalkGround): { player: PlayerState; fall: WalkFall | null } {
  const next = { ...player }; delete next.movementFall;
  const direction = Math.sign(dx);
  if (direction) next.face = direction < 0 ? -1 : 1;
  if (!next.alive || !direction) return { player: next, fall: null };
  let remaining = Math.abs(dx);
  while (remaining > 0 && next.energy > 0) {
    // GameLiving.canMoveDirection includes the source17-pixel edge margin.
    if (next.x + direction * 17 < 0 || next.x + direction * 17 > ground.width) break;
    const step = Math.min(SOURCE_WALK_STEP, remaining), x = next.x + direction * step;
    const supported = sourceStandingY(ground, x, next.y - SOURCE_WALK_HEIGHT - 1);
    if (supported !== null && Math.abs(supported - next.y) <= SOURCE_WALK_HEIGHT) {
      next.x = x; next.y = supported; next.energy = Math.max(0, next.energy - step); remaining -= step; continue;
    }
    const edgeStep = Math.min(SOURCE_EDGE_STEP, remaining), edgeX = next.x + direction * edgeStep;
    if (ground.isSolid(edgeX - 1, next.y) || ground.isSolid(edgeX + 1, next.y)) break;
    const toY = sourceStandingY(ground, edgeX, next.y - SOURCE_WALK_HEIGHT);
    const fromY = next.y;
    next.x = edgeX; next.energy = Math.max(0, next.energy - edgeStep);
    if (toY === null) {
      next.y = ground.height; next.alive = false;
      return { player: next, fall: { from: { x: edgeX, y: fromY }, to: { x: edgeX, y: ground.height },
        duration: sourceFallDuration(fromY, ground.height), hpBeforeDeath: next.hp } };
    }
    next.y = toY;
    const duration = sourceFallDuration(fromY, toY);
    return { player: next, fall: duration ? { from: { x: edgeX, y: fromY }, to: { x: edgeX, y: toY }, duration } : null };
  }
  return { player: next, fall: null };
}
